import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Trash2, PauseCircle, PlayCircle, Info } from "lucide-react";
import { api, apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const STATUS = {
  pending: ["In scadenza", "bg-amber-50 text-amber-800 border-amber-200"],
  hold: ["Sospesa", "bg-sky-50 text-sky-700 border-sky-200"],
  processing: ["In corso", "bg-indigo-50 text-indigo-700 border-indigo-200"],
  error: ["Errore", "bg-red-50 text-red-700 border-red-200"],
  suspended: ["Mapping dubbio", "bg-orange-50 text-orange-800 border-orange-200"],
  done: ["Cancellata", "bg-neutral-100 text-neutral-700 border-neutral-200"],
  cancelled: ["Annullata (riattivato)", "bg-emerald-50 text-emerald-700 border-emerald-200"],
};

const d = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");

function HoldForm({ c, onDone }) {
  const [reason, setReason] = useState("");
  const [until, setUntil] = useState("");
  const submit = async () => {
    try {
      await api.post(`/admin/media-retention/${c.case_id}/hold`, { reason, until: new Date(until).toISOString() });
      toast.success("Sospensione registrata");
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  return (
    <div className="mt-2 flex flex-wrap gap-2" data-testid={`mr-hold-form-${c.case_id}`}>
      <Input className="h-8 w-64" placeholder="Motivazione (min. 10 caratteri)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`mr-hold-reason-${c.case_id}`} />
      <Input className="h-8 w-40" type="date" value={until} onChange={(e) => setUntil(e.target.value)} data-testid={`mr-hold-until-${c.case_id}`} />
      <Button size="sm" onClick={submit} disabled={reason.trim().length < 10 || !until} data-testid={`mr-hold-submit-${c.case_id}`}>Sospendi fino a</Button>
    </div>
  );
}

function CaseRow({ c, reload }) {
  const [open, setOpen] = useState(false);
  const [label, cls] = STATUS[c.status] || [c.status, ""];
  const release = async () => {
    try {
      await api.post(`/admin/media-retention/${c.case_id}/release`);
      toast.success("Pratica rimessa in coda");
      reload();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const canHold = ["pending", "hold", "error", "suspended"].includes(c.status);
  const canRelease = ["hold", "error", "suspended"].includes(c.status);
  return (
    <tr className="border-t border-neutral-100 align-top" data-testid={`mr-case-${c.case_id}`}>
      <td className="px-3 py-2 font-medium">{c.name_salone || "—"}</td>
      <td className="px-3 py-2">{d(c.ceased_at)}</td>
      <td className="px-3 py-2 font-medium" data-testid={`mr-due-${c.case_id}`}>{d(c.due_at)}</td>
      <td className="px-3 py-2">
        <span className={`inline-block rounded-full border px-2 py-0.5 text-xs ${cls}`} data-testid={`mr-status-${c.case_id}`}>{label}</span>
        {c.hold && <div className="mt-1 text-xs text-neutral-500">fino al {d(c.hold.until)}: {c.hold.reason}</div>}
        {c.last_error && <div className="mt-1 text-xs text-red-600">{c.last_error} (tentativi {c.attempts})</div>}
      </td>
      <td className="px-3 py-2 text-right">
        {canHold && <Button size="sm" variant="ghost" onClick={() => setOpen(!open)} data-testid={`mr-hold-toggle-${c.case_id}`}><PauseCircle className="h-4 w-4" /></Button>}
        {canRelease && <Button size="sm" variant="ghost" onClick={release} data-testid={`mr-release-${c.case_id}`}><PlayCircle className="h-4 w-4" /></Button>}
        {open && <HoldForm c={c} onDone={() => { setOpen(false); reload(); }} />}
      </td>
    </tr>
  );
}

export const MediaRetentionPanel = () => {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/media-retention");
      setData(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  return (
    <section className="space-y-3" data-testid="media-retention-panel">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold"><Trash2 className="h-5 w-5" /> Cancellazione media dopo cessazione Pro</h2>
        <Button size="sm" variant="outline" onClick={load} data-testid="mr-refresh"><RefreshCw className="h-4 w-4" /></Button>
      </div>
      <div className="grid gap-2 text-sm sm:grid-cols-3">
        <div className="rounded-lg border bg-white p-3" data-testid="mr-flag">Automatismo: <b>{data.autodelete_flag ? "attivo" : "disattivo"}</b> · {data.scheduler}</div>
        <div className="rounded-lg border bg-white p-3">Scadenza: <b>{data.retention_days} giorni</b> dalla cessazione effettiva registrata</div>
        <div className="rounded-lg border bg-white p-3" data-testid="mr-historical">Clienti non Pro senza cessazione registrata (storici inclusi): <b>{data.historical_without_date}</b> — nessuna scadenza, nessuna cancellazione</div>
      </div>
      <div className="flex gap-2 rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900" data-testid="mr-backup-note">
        <Info className="mt-0.5 h-4 w-4 shrink-0" /><span>{data.backup_note}</span>
      </div>
      <div className="overflow-x-auto rounded-xl border bg-white">
        <table className="w-full text-sm" data-testid="mr-cases-table">
          <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wide text-neutral-500">
            <tr><th className="px-3 py-2">Salone</th><th className="px-3 py-2">Cessazione</th><th className="px-3 py-2">Cancellazione prevista</th><th className="px-3 py-2">Stato</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody>
            {data.cases.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-neutral-500">Nessuna cessazione registrata.</td></tr>}
            {data.cases.map((c) => <CaseRow key={c.case_id} c={c} reload={load} />)}
          </tbody>
        </table>
      </div>
    </section>
  );
};
